import { useState } from 'react'
import { Card, Button, Checkbox, Select, Radio, Table, Tag, Space, message, Tabs, Input, Statistic, Row, Col } from 'antd'
import { DownloadOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons'
import type { DatasetExportTask } from '../../types/admin'
import { mockDatasetTasks } from '../../services/mockData'

const { Option } = Select
const { TabPane } = Tabs
const { TextArea } = Input

const DatasetDownload = () => {
  const [tasks, setTasks] = useState<DatasetExportTask[]>(mockDatasetTasks)
  const [exportConfig, setExportConfig] = useState({
    contents: ['doc_meta', 'question_details', 'reply_details'],
    format: 'jsonl',
    sftFormat: 'alpaca',
    includeCoT: true,
    separateRationale: true,
    desensitization: 'none',
  })

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'taskId',
      key: 'taskId',
      render: (text: string) => <span className="font-mono text-sm">{text}</span>,
    },
    {
      title: '任务名称',
      dataIndex: 'taskName',
      key: 'taskName',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: '数据量',
      key: 'data',
      render: (_: any, record: DatasetExportTask) => (
        <span>{record.totalCases} 案件</span>
      ),
    },
    {
      title: '格式',
      key: 'format',
      render: (_: any, record: DatasetExportTask) => (
        <Space>
          {record.exportConfig.formats.map(fmt => (
            <Tag key={fmt}>{fmt.toUpperCase()}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const config: Record<string, { color: string; text: string }> = {
          pending: { color: 'default', text: '等待中' },
          processing: { color: 'processing', text: '处理中' },
          packaging: { color: 'warning', text: '打包中' },
          completed: { color: 'success', text: '完成' },
          failed: { color: 'error', text: '失败' },
        }
        return <Tag color={config[status]?.color}>{config[status]?.text}</Tag>
      },
    },
    {
      title: '大小',
      key: 'size',
      render: (_: any, record: DatasetExportTask) => (
        record.fileSize ? `${(record.fileSize / 1024 / 1024 / 1024).toFixed(2)} GB` : '--'
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: DatasetExportTask) => (
        <Space>
          {record.status === 'completed' && (
            <Button type="primary" icon={<DownloadOutlined />}>
              下载
            </Button>
          )}
          <Button danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  const handleCreateTask = () => {
    message.success('导出任务已创建')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">📦 数据集下载管理</h1>
      </div>

      {/* 数据概览 */}
      <Row gutter={16}>
        <Col span={6}>
          <Card>
            <Statistic title="总案件数" value={1256} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="已标注案件" value={1180} suffix="(94%)" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="已通过质检" value={1156} suffix="(92%)" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="QA对总数" value={3420} />
          </Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="export">
        <TabPane tab="新建导出" key="export">
          <Card title="🔍 数据筛选与导出配置">
            {/* 数据范围 */}
            <div className="mb-6">
              <h4 className="font-medium mb-3">数据范围</h4>
              <Radio.Group defaultValue="all">
                <Radio value="all">全部数据</Radio>
                <Radio value="time">按时间范围</Radio>
                <Radio value="case">按案件ID</Radio>
                <Radio value="quality">按标注质量</Radio>
              </Radio.Group>
            </div>

            {/* 导出内容 */}
            <div className="mb-6">
              <h4 className="font-medium mb-3">📋 导出内容</h4>
              <Checkbox.Group 
                value={exportConfig.contents}
                onChange={(values) => setExportConfig({...exportConfig, contents: values as string[]})}
              >
                <Space direction="vertical">
                  <Checkbox value="doc_meta">文档元数据 (doc_meta)</Checkbox>
                  <Checkbox value="question_details">质疑事项 (question_details)</Checkbox>
                  <Checkbox value="reply_details">答复事项 (question_reply_details)</Checkbox>
                  <Checkbox value="ner">NER实体标注</Checkbox>
                  <Checkbox value="relations">实体关系三元组</Checkbox>
                  <Checkbox value="raw_docs">原始文档 (PDF/DOCX)</Checkbox>
                  <Checkbox value="cleaned_text">清洗后文本</Checkbox>
                </Space>
              </Checkbox.Group>
            </div>

            {/* 数据集格式 */}
            <div className="mb-6">
              <h4 className="font-medium mb-3">📁 数据集格式</h4>
              <Radio.Group 
                value={exportConfig.format}
                onChange={(e) => setExportConfig({...exportConfig, format: e.target.value})}
              >
                <Space>
                  <Radio.Button value="json">JSON</Radio.Button>
                  <Radio.Button value="jsonl">JSONL (SFT)</Radio.Button>
                  <Radio.Button value="csv">CSV</Radio.Button>
                  <Radio.Button value="excel">Excel</Radio.Button>
                  <Radio.Button value="parquet">Parquet</Radio.Button>
                  <Radio.Button value="huggingface">HuggingFace</Radio.Button>
                </Space>
              </Radio.Group>
            </div>

            {/* SFT训练格式配置 */}
            {exportConfig.format === 'jsonl' && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3">🤖 SFT训练格式配置</h4>
                <div className="space-y-3">
                  <div>
                    <span className="mr-4">格式类型:</span>
                    <Radio.Group 
                      value={exportConfig.sftFormat}
                      onChange={(e) => setExportConfig({...exportConfig, sftFormat: e.target.value})}
                    >
                      <Radio value="alpaca">Alpaca</Radio>
                      <Radio value="sharegpt">ShareGPT</Radio>
                      <Radio value="openai">OpenAI</Radio>
                    </Radio.Group>
                  </div>
                  <div>
                    <Checkbox 
                      checked={exportConfig.includeCoT}
                      onChange={(e) => setExportConfig({...exportConfig, includeCoT: e.target.checked})}
                    >
                      包含思维链 (CoT)
                    </Checkbox>
                  </div>
                  <div>
                    <Checkbox 
                      checked={exportConfig.separateRationale}
                      onChange={(e) => setExportConfig({...exportConfig, separateRationale: e.target.checked})}
                    >
                      分离Rationale和Conclusion
                    </Checkbox>
                  </div>
                </div>
              </div>
            )}

            {/* 数据脱敏 */}
            <div className="mb-6">
              <h4 className="font-medium mb-3">🔐 数据脱敏</h4>
              <Radio.Group 
                value={exportConfig.desensitization}
                onChange={(e) => setExportConfig({...exportConfig, desensitization: e.target.value})}
              >
                <Radio value="none">保持原样</Radio>
                <Radio value="light">轻度脱敏 (保留实体类型)</Radio>
                <Radio value="full">完全脱敏</Radio>
              </Radio.Group>
            </div>

            {/* 操作按钮 */}
            <div className="flex justify-center gap-4">
              <Button>预览数据样本</Button>
              <Button>统计信息</Button>
              <Button type="primary" icon={<DownloadOutlined />} onClick={handleCreateTask}>
                生成下载
              </Button>
            </div>
          </Card>

          {/* 数据样本预览 */}
          <Card title="📋 数据样本预览 (JSONL/SFT格式)" className="mt-6">
            <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto text-sm">
{`{
  "instruction": "你是一位招投标质疑处理专家。请根据质疑事项，提供专业的答复。",
  "input": "质疑事项：中标人公告报价与现场唱标折扣率不一致。现场开标时投影仪显示综合折扣率为95%，但结果公告显示为4.75%，存在明显差异。",
  "output": {
    "reasoning": "经核查，现场唱标记录与公告结果一致，质疑方所提及的'95%'为投影显示错误，实际唱标记录为4.75%。评标过程符合法定程序，不存在报价不一致的情况。",
    "conclusion": "维持原中标结果，不予修改。"
  },
  "metadata": {
    "case_id": "CASE-2024-0001",
    "topic": "报价评审",
    "reply_position": "不采纳"
  }
}`}
            </pre>
          </Card>
        </TabPane>

        <TabPane tab="下载任务" key="tasks">
          <Card
            title="📥 下载任务列表"
            extra={
              <Space>
                <Button icon={<ReloadOutlined />}>刷新</Button>
                <Button danger>清理已完成</Button>
              </Space>
            }
          >
            <Table
              columns={columns}
              dataSource={tasks}
              rowKey="taskId"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  )
}

export default DatasetDownload
